// Technology stack vendor detection. Maps vendor mentions in
// `enrichment.technologyProfile` (or in client_technology_stack records)
// to industry-specific supply-chain risk and named-actor relevance.
//
// Vendors are categorized by what they expose if compromised (industry SaaS,
// identity, edge appliance, file transfer, OT/BMS, etc).

const VENDOR_CATEGORIES = {
  // Property management SaaS — RE supply chain
  yardi: { category: 'property_mgmt_saas', industries: ['TP-RE'], aliases: ['yardione'] },
  realpage: { category: 'property_mgmt_saas', industries: ['TP-RE'] },
  mri_software: { category: 'property_mgmt_saas', industries: ['TP-RE'], aliases: ['mri'] },
  appfolio: { category: 'property_mgmt_saas', industries: ['TP-RE'] },
  buildium: { category: 'property_mgmt_saas', industries: ['TP-RE'] },

  // EHR / healthcare SaaS
  epic: { category: 'ehr_platform', industries: ['TP-HC'] },
  cerner: { category: 'ehr_platform', industries: ['TP-HC'], aliases: ['oracle health'] },
  athenahealth: { category: 'ehr_platform', industries: ['TP-HC'] },
  meditech: { category: 'ehr_platform', industries: ['TP-HC'] },
  allscripts: { category: 'ehr_platform', industries: ['TP-HC'] },

  // Core banking / financial SaaS
  fis: { category: 'core_banking_saas', industries: ['TP-FIN-BK'] },
  fiserv: { category: 'core_banking_saas', industries: ['TP-FIN-BK'] },
  jack_henry: { category: 'core_banking_saas', industries: ['TP-FIN-BK'], aliases: ['symitar'] },

  // Education SaaS
  canvas: { category: 'lms_platform', industries: ['TP-EDU'], aliases: ['instructure'] },
  blackboard: { category: 'lms_platform', industries: ['TP-EDU'] },
  powerschool: { category: 'sis_platform', industries: ['TP-EDU'] },
  infinite_campus: { category: 'sis_platform', industries: ['TP-EDU'] },

  // File transfer products (Cl0p targeting — universal high concern)
  moveit: { category: 'file_transfer', actorAlerts: ['cl0p'] },
  goanywhere: { category: 'file_transfer', actorAlerts: ['cl0p'] },
  cleo: { category: 'file_transfer', actorAlerts: ['cl0p'] },
  accellion: { category: 'file_transfer', actorAlerts: ['cl0p'] },

  // Edge appliances (universal ransomware initial access)
  fortinet: { category: 'edge_appliance', actorAlerts: ['lockbit', 'play_ransomware', 'volt_typhoon'] },
  fortigate: { category: 'edge_appliance', actorAlerts: ['lockbit', 'play_ransomware'] },
  cisco_asa: { category: 'edge_appliance', actorAlerts: ['akira', 'volt_typhoon'], aliases: ['cisco anyconnect'] },
  citrix_netscaler: { category: 'edge_appliance', actorAlerts: ['lockbit'], aliases: ['netscaler'] },
  ivanti: { category: 'edge_appliance', actorAlerts: ['lockbit'], aliases: ['pulse secure', 'connect secure'] },

  // Identity providers (Scattered Spider, Lapsus$, Midnight Blizzard)
  okta: { category: 'idp', actorAlerts: ['scattered_spider', 'lapsus'] },
  microsoft_entra: { category: 'idp', actorAlerts: ['scattered_spider', 'lapsus', 'midnight_blizzard'], aliases: ['azure ad', 'azure active directory', 'entra id'] },
  ping_identity: { category: 'idp', actorAlerts: ['scattered_spider'] },
  duo: { category: 'mfa', actorAlerts: ['scattered_spider'] },

  // Virtualization / backup (ALPHV ESXi targeting)
  vmware_esxi: { category: 'hypervisor', actorAlerts: ['alphv_blackcat', 'akira'], aliases: ['esxi', 'vsphere', 'vcenter'] },
  veeam: { category: 'backup', actorAlerts: ['alphv_blackcat', 'akira'] },

  // Help desk / RMM (Scattered Spider lateral movement)
  servicenow: { category: 'itsm', actorAlerts: ['scattered_spider'] },
  connectwise: { category: 'rmm', actorAlerts: ['alphv_blackcat'], aliases: ['screenconnect'] },
  kaseya: { category: 'rmm' }
}

function findVendorMatches(vendorMentions = []) {
  const haystack = vendorMentions
    .filter(Boolean)
    .map(v => String(v).toLowerCase())
    .join(' \n ')

  const matched = []
  for (const [key, def] of Object.entries(VENDOR_CATEGORIES)) {
    const candidates = [key.replace(/_/g, ' '), ...(def.aliases || [])]
    for (const c of candidates) {
      if (haystack.includes(c.toLowerCase())) {
        matched.push({ key, ...def })
        break
      }
    }
  }
  return matched
}

// Collect raw vendor strings from every enrichment surface we have. Treat
// all of them as one bag for fuzzy substring match.
function collectVendorMentions(enrichment, technologyStack = []) {
  const tp = enrichment?.technologyProfile || {}
  const mentions = []

  ;['emailPlatform', 'cloudPlatform', 'identityPlatform', 'dnsProvider', 'hostingProvider', 'websiteTechnology'].forEach(k => {
    if (tp[k]) mentions.push(tp[k])
  })

  if (Array.isArray(tp.detectedServices)) mentions.push(...tp.detectedServices)
  if (Array.isArray(tp.otherTools)) mentions.push(...tp.otherTools)

  // Client-curated technology stack rows (advisor-entered)
  for (const t of technologyStack || []) {
    if (t?.name) mentions.push(t.name)
    if (t?.vendor) mentions.push(t.vendor)
  }
  return mentions
}

export function detectTechStackSignals({ enrichment, technologyStack = [], profileKey } = {}) {
  const adj = { attackVectors: {}, targetAssets: {}, threatActors: {} }
  const evidence = []
  const namedActorAlerts = new Set()

  const mentions = collectVendorMentions(enrichment, technologyStack)
  if (mentions.length === 0) return { adjustments: adj, evidence, vendorMatches: [], namedActorAlerts: [] }

  const matches = findVendorMatches(mentions)

  for (const m of matches) {
    if (m.category === 'property_mgmt_saas') {
      adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.15
      evidence.push(`Property management SaaS in stack (${m.key.replace(/_/g, ' ')}) — concentrated supply-chain risk; vendors in this category have suffered notable breaches affecting downstream tenants`)
    }
    if (m.category === 'ehr_platform') {
      adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.15
      adj.targetAssets.patient_data = (adj.targetAssets.patient_data || 0) + 0.10
      evidence.push(`EHR platform in stack (${m.key}) — extension of patient-data trust boundary; ransomware actors actively target this category`)
    }
    if (m.category === 'core_banking_saas') {
      adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.15
      evidence.push(`Core banking platform (${m.key}) — high-value supply chain; nation-state and financial-fraud actors actively target`)
    }
    if (m.category === 'lms_platform' || m.category === 'sis_platform') {
      adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.10
      evidence.push(`Education platform (${m.key}) — student data concentration; Rhysida and Vice Society lineage actively targeted`)
    }
    if (m.category === 'file_transfer') {
      adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.20
      evidence.push(`Managed file transfer product (${m.key}) — Cl0p has historically zero-day'd this category. Verify patch cadence and exposure`)
    }
    if (m.category === 'edge_appliance') {
      adj.attackVectors.unpatched_systems = (adj.attackVectors.unpatched_systems || 0) + 0.15
      evidence.push(`Edge appliance (${m.key}) — frequent ransomware initial-access vector. Multiple active groups exploit this category`)
    }
    if (m.category === 'idp') {
      adj.attackVectors.social_engineering = (adj.attackVectors.social_engineering || 0) + 0.10
      adj.attackVectors.credential_theft = (adj.attackVectors.credential_theft || 0) + 0.10
      evidence.push(`Identity provider (${m.key}) — primary target for help-desk vishing and MFA-fatigue attacks (Scattered Spider, Lapsus$)`)
    }
    if (m.category === 'hypervisor') {
      adj.attackVectors.unpatched_systems = (adj.attackVectors.unpatched_systems || 0) + 0.15
      evidence.push(`Hypervisor exposure (${m.key}) — ALPHV/BlackCat and Akira actively target ESXi environments; verify management interfaces are not internet-exposed`)
    }
    if (m.category === 'backup') {
      adj.attackVectors.ransomware_recovery_inhibitor = (adj.attackVectors.ransomware_recovery_inhibitor || 0) + 0.10
      evidence.push(`Backup product (${m.key}) — ALPHV/BlackCat and Akira have known credential-extraction techniques targeting this category`)
    }

    if (Array.isArray(m.actorAlerts)) {
      m.actorAlerts.forEach(a => namedActorAlerts.add(a))
    }
  }

  // Profile-specific filtering: only emit industries that match this profile,
  // OR universal categories (no industries field).
  if (profileKey) {
    // No-op for now — the evidence strings themselves stay. Future: filter to
    // suppress evidence rows for industries unrelated to this profile.
  }

  return { adjustments: adj, evidence, vendorMatches: matches, namedActorAlerts: Array.from(namedActorAlerts) }
}
